import { fetchRoute } from './route';
import { appendRouteEvents, mergeRoutePartial } from './routeMerge';
import { routeStore } from '../state/routeStore';

/**
 * Reconciliation net for missed socket-room events.
 *
 * The socket is now the primary channel for everything — location (#156),
 * emergency (#143), transportation (#149/#151), title/destination edits
 * (#159) and journey events (#180) all fan out to the routes/{routeID}
 * room. But Socket.IO rooms don't replay: anything emitted while the tab
 * was asleep, the phone was backgrounded, or Cloudflare dropped the
 * WebSocket is gone for good. This poll re-reads `GET /v3/route/:id` on a
 * slow cadence (plus once on reconnect via `pollNow`) and merges whatever
 * the live stream missed. It is also the only source of
 * `emergency.alertedCount`, which the socket events don't carry.
 */

const POLL_INTERVAL_MS = 30_000;

let intervalId: ReturnType<typeof setInterval> | null = null;
let activeRouteId: string | null = null;
let visibilityHandler: (() => void) | null = null;

function shouldPoll(): boolean {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return false;
  const route = routeStore.value;
  if (!route || route.finished) return false;
  return true;
}

async function pollOnce(routeId: string): Promise<void> {
  if (!shouldPoll()) return;
  try {
    const fresh = await fetchRoute(routeId);
    const current = routeStore.value;
    if (!current || current.id !== fresh.id) return; // user navigated or stale callback
    const currentWaypoints = current.waypoints ?? [];
    const freshWaypoints = fresh.waypoints ?? [];
    const waypoints =
      freshWaypoints.length > currentWaypoints.length ? freshWaypoints : currentWaypoints;
    if (waypoints !== currentWaypoints && import.meta.env.DEV) {
      console.log('[route:event] route:poll:waypoints', {
        routeId,
        previous: currentWaypoints.length,
        next: waypoints.length,
      });
    }
    // Waypoints use longer-wins: the REST doc is the authority set, but a
    // socket burst between request and response can put the local copy
    // ahead — never truncate it. Events are unioned through the same
    // type+time dedupe the socket listener uses, so an event only one side
    // has (a missed broadcast, or a pre-save socket row the REST read
    // doesn't carry yet) is never lost. Everything else goes through the
    // partial merge shared with the socket listeners.
    routeStore.value = appendRouteEvents(
      { ...mergeRoutePartial(current, fresh), waypoints },
      fresh.events ?? [],
    );
  } catch {
    // Polling failures are silent — the next tick will retry. We don't want
    // a transient network blip to surface as a banner-style error.
  }
}

export function startRoutePoll(routeId: string): void {
  stopRoutePoll();
  activeRouteId = routeId;

  // Tick once immediately on visibility change so a returning watcher sees the
  // latest state right away rather than waiting up to 30s for the next interval.
  if (typeof document !== 'undefined') {
    visibilityHandler = () => {
      if (document.visibilityState === 'visible' && activeRouteId) void pollOnce(activeRouteId);
    };
    document.addEventListener('visibilitychange', visibilityHandler);
  }

  intervalId = setInterval(() => {
    if (activeRouteId) void pollOnce(activeRouteId);
  }, POLL_INTERVAL_MS);
  void pollOnce(routeId);
}

/**
 * One-shot poll outside the interval — called by socket.ts the moment a
 * connection is re-established, so a watcher who slept through events is
 * reconciled immediately instead of waiting out the 30s cadence.
 */
export function pollNow(): void {
  if (activeRouteId) void pollOnce(activeRouteId);
}

export function stopRoutePoll(): void {
  if (intervalId !== null) clearInterval(intervalId);
  intervalId = null;
  if (visibilityHandler && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', visibilityHandler);
  }
  visibilityHandler = null;
  activeRouteId = null;
}
